import React, { useState, useEffect, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
    Modal, Form, Input, DatePicker, Button, Table, Space,
    Row, Col, Typography, Card, InputNumber, message,
    Popconfirm, Divider, Tag, Tooltip, Statistic, Progress, Select,
    Empty, Timeline, Switch
} from "antd";
import {
    PlusOutlined, DeleteOutlined, EditOutlined,
    SaveOutlined, AppstoreOutlined, PieChartOutlined,
    DollarCircleOutlined, CalendarOutlined, PercentageOutlined,
    CarOutlined, DesktopOutlined, HomeOutlined, BuildOutlined,
    HistoryOutlined, InfoCircleOutlined, ArrowUpOutlined, ArrowDownOutlined,
    SearchOutlined, FilePdfOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDraggableModal } from "../Balancete/utils/modalDraggable";
import { Method_POST_CONTEXTO } from "../../../../service/api";

const { Title, Text } = Typography;
const { Option } = Select;

export default function ModalPatrimonio({ visible, onClose, zIndex, onFocus, title }) {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        visible,
        top: 40,
        modalKey: 'patrimonio',
        initialWidth: 1400,
        initialHeight: 'auto',
        overflow: 'hidden'
    });
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [patrimonios, setPatrimonios] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [formModalVisible, setFormModalVisible] = useState(false);
    const [historicoVisible, setHistoricoVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const categorias = [
        { label: "Edificações (Alvenaria)", value: "IMOVEIS", icon: <HomeOutlined />, color: "#faad14", taxaMensal: 0.33 },
        { label: "Instalações", value: "INSTALACOES", icon: <BuildOutlined />, color: "#13c2c2", taxaMensal: 0.83 },
        { label: "Máquinas e Equipamentos", value: "MAQUINAS", icon: <AppstoreOutlined />, color: "#eb2f96", taxaMensal: 0.83 },
        { label: "Móveis e Utensílios", value: "MOVEIS", icon: <BuildOutlined />, color: "#1890ff", taxaMensal: 0.83 },
        { label: "Veículos (Passageiros/Carga)", value: "VEICULOS", icon: <CarOutlined />, color: "#52c41a", taxaMensal: 1.67 },
        { label: "Computadores e Periféricos", value: "TI", icon: <DesktopOutlined />, color: "#722ed1", taxaMensal: 1.67 },
        { label: "Ferramentas", value: "FERRAMENTAS", icon: <BuildOutlined />, color: "#fa8c16", taxaMensal: 2.08 },
        { label: "Outros", value: "OUTROS", icon: <InfoCircleOutlined />, color: "#8c8c8c", taxaMensal: 0 },
    ];

    useEffect(() => {
        if (visible) {
            fetchPatrimonios();
        }
    }, [visible]);

    const fetchPatrimonios = async () => {
        setLoading(true);
        try {
            const res = await Method_POST_CONTEXTO("empresa/contabilidade/patrimonio/listar");
            setPatrimonios(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error("Erro ao carregar patrimônio:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const payload = {
                id: editingId || undefined,
                ...values,
                dataAquisicao: values.dataAquisicao ? values.dataAquisicao.valueOf() : null,
            };

            await Method_POST_CONTEXTO("empresa/contabilidade/patrimonio/salvar", payload);
            message.success("Patrimônio registrado com sucesso!");
            setFormModalVisible(false);
            setEditingId(null);
            form.resetFields();
            fetchPatrimonios();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await Method_POST_CONTEXTO("empresa/contabilidade/patrimonio/deletar", { id });
            message.success("Bem removido do inventário.");
            fetchPatrimonios();
        } catch (err) {
            message.error("Erro ao processar exclusão.");
        }
    };

    const calculateDepreciation = (record) => {
        const meses = Math.max(0, dayjs().diff(dayjs(record.dataAquisicao), 'month'));
        const valorMes = (record.valorAquisicao * (record.taxaDepreciacaoMensal / 100));
        const acumulada = Math.min(record.valorAquisicao, meses * valorMes);
        const atual = record.valorAquisicao - acumulada;
        const percentual = Math.min(100, (acumulada / record.valorAquisicao) * 100);
        return { acumulada, atual, percentual, meses };
    };

    const filteredData = useMemo(() => {
        if (!searchTerm) return patrimonios;
        return patrimonios.filter(p => p.descricao?.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [patrimonios, searchTerm]);

    const columns = [
        {
            title: "CÓDIGO",
            dataIndex: "id",
            key: "id",
            width: 80,
            align: 'center',
            render: (id) => <Text type="secondary" style={{ fontSize: 12 }}>#{id}</Text>
        },
        {
            title: "BEM / PATRIMÔNIO",
            key: "bem",
            render: (_, record) => {
                const cat = categorias.find(c => c.value === record.categoria) || categorias[5];
                return (
                    <Space size="middle">
                        <div style={{
                            width: 32, height: 32, borderRadius: 6,
                            backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', color: cat.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            {cat.icon}
                        </div>
                        <div>
                            <Text strong style={{ display: 'block' }}>{record.descricao}</Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>{cat.label}</Text>
                        </div>
                    </Space>
                );
            }
        },
        {
            title: "AQUISIÇÃO",
            dataIndex: "dataAquisicao",
            key: "dataAquisicao",
            width: 140,
            render: (date) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text>{dayjs(date).format("DD/MM/YYYY")}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {dayjs().diff(dayjs(date), 'month')} meses
                    </Text>
                </div>
            ),
        },
        {
            title: "VALOR AQUISIÇÃO",
            key: "valor",
            width: 160,
            render: (_, record) => (
                <Text strong>{record.valorAquisicao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Text>
            )
        },
        {
            title: "DEPRECIAÇÃO ACUM.",
            key: "depreciacao",
            width: 220,
            render: (_, record) => {
                const { acumulada, percentual } = calculateDepreciation(record);
                return (
                    <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <Text danger style={{ fontSize: 11 }}>{acumulada.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Text>
                            <Text style={{ fontSize: 11 }}>{percentual.toFixed(2)}%</Text>
                        </div>
                        <Progress percent={parseFloat(percentual.toFixed(2))} size="small" strokeColor={percentual > 80 ? '#f5222d' : '#1890ff'} showInfo={false} />
                    </div>
                );
            }
        },
        {
            title: "VALOR ATUAL",
            key: "valorAtual",
            width: 160,
            render: (_, record) => {
                const { atual } = calculateDepreciation(record);
                return <Text strong style={{ color: '#52c41a' }}>{atual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Text>;
            }
        },
        {
            title: "AÇÕES",
            key: "action",
            align: 'center',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(record.id);
                            form.setFieldsValue({
                                ...record,
                                dataAquisicao: record.dataAquisicao ? dayjs(record.dataAquisicao) : null,
                                lancamentoAutomatico: record.lancamentoAutomatico !== undefined ? record.lancamentoAutomatico : true
                            });
                            setFormModalVisible(true);
                        }}
                        style={{ color: '#005A9E', fontWeight: 600 }}
                    >
                        Editar
                    </Button>
                    <Popconfirm title="Excluir este bem?" onConfirm={() => handleDelete(record.id)} okText="Sim" cancelText="Não">
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ fontWeight: 600 }}>
                            Excluir
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const totals = useMemo(() => {
        return patrimonios.reduce((acc, curr) => {
            const { acumulada, atual } = calculateDepreciation(curr);
            acc.total += curr.valorAquisicao;
            acc.acumulada += acumulada;
            acc.atual += atual;
            return acc;
        }, { total: 0, acumulada: 0, atual: 0 });
    }, [patrimonios]);

    const historicoData = useMemo(() => {
        const history = [];
        patrimonios.forEach(p => {
            if (p.dataAquisicao) {
                history.push({
                    id: `add_${p.id}`,
                    date: dayjs(p.dataAquisicao).format('DD/MM/YYYY'),
                    timestamp: dayjs(p.dataAquisicao).valueOf(),
                    title: 'Aquisição de Patrimônio',
                    desc: `O bem "${p.descricao}" foi adicionado ao inventário por ${p.valorAquisicao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`,
                    color: 'green'
                });
            }
        });
        return history.sort((a, b) => b.timestamp - a.timestamp);
    }, [patrimonios]);

    const handleExportPDF = () => {
        const doc = new jsPDF('landscape');

        doc.setFontSize(18);
        doc.setTextColor(0, 90, 158);
        doc.text("Relatório de Patrimônio Imobilizado", 14, 22);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Gerado em: ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 30);

        doc.setFontSize(11);
        doc.setTextColor(0);
        doc.text(`Valor Total em Bens: ${totals.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 14, 40);
        doc.text(`Desvalorização Acumulada: ${totals.acumulada.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 100, 40);
        doc.text(`Valor Líquido Atual: ${totals.atual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 190, 40);

        const tableColumn = ["CÓDIGO", "BEM / PATRIMÔNIO", "CATEGORIA", "AQUISIÇÃO", "VALOR AQUISIÇÃO", "TAXA MENSAL", "DEP. ACUM.", "VALOR ATUAL"];
        const tableRows = [];

        filteredData.forEach(p => {
            const cat = categorias.find(c => c.value === p.categoria) || categorias[5];
            const { acumulada, atual, percentual } = calculateDepreciation(p);

            const pData = [
                `#${p.id}`,
                p.descricao,
                cat.label,
                dayjs(p.dataAquisicao).format("DD/MM/YYYY"),
                p.valorAquisicao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
                `${p.taxaDepreciacaoMensal}%`,
                `${acumulada.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} (${percentual.toFixed(2)}%)`,
                atual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            ];
            tableRows.push(pData);
        });

        autoTable(doc, {
            startY: 48,
            head: [tableColumn],
            body: tableRows,
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [0, 90, 158], textColor: 255 }
        });

        doc.save(`relatorio_patrimonio_${dayjs().format('YYYYMMDD_HHmm')}.pdf`);
        message.success("Relatório PDF gerado com sucesso!");
    };

    return (
        <Modal
            title={title || (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <BuildOutlined style={{ color: '#005A9E' }} />
                    <span>Controle de Patrimônio Imobilizado</span>
                </div>
            )}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1600}
            {...draggableProps}
            styles={{
                body: {
                    maxHeight: "80vh",
                    padding: '20px 24px',
                    overflowY: "auto",
                    backgroundColor: '#fff'
                }
            }}
        >
            <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col span={6}>
                    <Card size="small" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <Statistic
                            title="Valor em Bens"
                            value={totals.total}
                            formatter={(val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val)}
                            prefix={<DollarCircleOutlined style={{ fontSize: 16 }} />}
                            valueStyle={{ fontSize: 18, fontWeight: 700 }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <Statistic
                            title="Desvalorização"
                            value={totals.acumulada}
                            formatter={(val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val)}
                            prefix={<ArrowDownOutlined style={{ color: '#cf1322', fontSize: 16 }} />}
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <Statistic
                            title="Valor Líquido Atual"
                            value={totals.atual}
                            formatter={(val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val)}
                            prefix={<ArrowUpOutlined style={{ color: '#3f8600', fontSize: 16 }} />}
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
                        <Button icon={<HistoryOutlined />} onClick={() => setHistoricoVisible(true)}>Histórico</Button>
                        <Button icon={<FilePdfOutlined />} onClick={handleExportPDF}>Relatório</Button>
                    </div>
                </Col>
            </Row>

            <Divider style={{ margin: '12px 0 24px 0' }} />

            <div style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle" justify="space-between">
                    <Col>
                        <Space>
                            <Input
                                placeholder="Pesquisar por descrição..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{ width: 350, height: 40, borderRadius: 8 }}
                                prefix={<SearchOutlined style={{ color: '#64748b' }} />}
                            />
                            <Button type="primary" onClick={fetchPatrimonios} style={{ height: 40, borderRadius: 8, backgroundColor: '#005A9E' }}>Buscar</Button>
                        </Space>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setEditingId(null);
                                form.resetFields();
                                setFormModalVisible(true);
                            }}
                            style={{ height: 40, borderRadius: 8, backgroundColor: '#005A9E', padding: '0 24px', fontWeight: 600 }}
                        >
                            Novo Patrimônio
                        </Button>
                    </Col>
                </Row>
            </div>

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 15 }}
                size="middle"
                className="accounting-table"
                scroll={{ y: '500px' }}
            />

            {/* Sub-Modal de Formulário - BOOSTED Z-INDEX */}
            <Modal
                title={null}
                open={formModalVisible}
                onCancel={() => setFormModalVisible(false)}
                footer={null}
                width={750}
                destroyOnClose
                className="premium-modal-v2"
                zIndex={999999}
                mask={true}
                maskStyle={{ backgroundColor: 'rgba(0,0,0,0.15)' }}
            >
                <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 12, backgroundColor: '#f0f9ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#005A9E', fontSize: 24
                    }}>
                        {editingId ? <EditOutlined /> : <PlusOutlined />}
                    </div>
                    <div>
                        <Title level={4} style={{ margin: 0 }}>{editingId ? "Editar Patrimônio" : "Novo Patrimônio"}</Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>Informe os detalhes abaixo para registro contábil e depreciação.</Text>
                    </div>
                </div>

                <Divider style={{ margin: '16px 0 24px 0' }} />

                <Form form={form} layout="vertical" initialValues={{ gerarLancamento: true }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Nome / Descrição" name="descricao" rules={[{ required: true, message: 'Campo obrigatório' }]}>
                                <Input placeholder="Ex: Notebook Dell Vostro" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Categoria" name="categoria" rules={[{ required: true, message: 'Selecione uma categoria' }]}>
                                <Select
                                    placeholder="Selecione..."
                                    onChange={(value) => {
                                        const cat = categorias.find(c => c.value === value);
                                        if (cat && form) {
                                            form.setFieldValue("taxaDepreciacaoMensal", cat.taxaMensal);
                                        }
                                    }}
                                >
                                    {categorias.map(c => (
                                        <Option key={c.value} value={c.value}>
                                            <Space>{c.icon} {c.label}</Space>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Data de Aquisição" name="dataAquisicao" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Valor de Compra (R$)" name="valorAquisicao" rules={[{ required: true }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    decimalSeparator=","
                                    formatter={val => val !== undefined && val !== null ? `R$ ${val}`.replace(/\./g, ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''}
                                    parser={val => val ? val.replace(/\R\$\s?|\./g, '').replace(',', '.') : ''}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Depreciação Mensal (%)" name="taxaDepreciacaoMensal" rules={[{ required: true }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0} max={100} step={0.1}
                                    decimalSeparator=","
                                    formatter={val => val !== undefined && val !== null ? `${val}%`.replace('.', ',') : ''}
                                    parser={val => val ? val.replace('%', '').replace(',', '.') : ''}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16} align="middle" style={{ marginTop: 8 }}>
                        <Col span={24}>
                            <Form.Item name="gerarLancamento" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => form.setFieldValue('gerarLancamento', !form.getFieldValue('gerarLancamento'))}>
                                    <Switch checked={form.getFieldValue('gerarLancamento')} />
                                    <div>
                                        <Text strong style={{ display: 'block', color: '#005A9E' }}>Automatizar Lançamento Contábil</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>O sistema irá gerar automaticamente os lançamentos de depreciação mensal deste bem na contabilidade.</Text>
                                    </div>
                                </div>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '32px 0 16px 0' }} />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={(e) => { e.stopPropagation(); setFormModalVisible(false); }} style={{ borderRadius: 6, fontWeight: 600 }}>
                            Cancelar
                        </Button>
                        <Button type="primary" onClick={handleSave} loading={loading} style={{ borderRadius: 6, fontWeight: 600, padding: '0 32px', backgroundColor: '#005A9E' }}>
                            {editingId ? "Salvar Alterações" : "Salvar Patrimônio"}
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <HistoryOutlined style={{ color: '#005A9E', fontSize: 20 }} />
                        <span style={{ fontSize: 16 }}>Histórico de Movimentações</span>
                    </div>
                }
                open={historicoVisible}
                onCancel={() => setHistoricoVisible(false)}
                footer={
                    <Button onClick={() => setHistoricoVisible(false)} style={{ borderRadius: 6, fontWeight: 600 }}>Fechar</Button>
                }
                width={700}
                zIndex={999999}
                destroyOnClose
            >
                <div style={{ padding: '24px 0 10px 0', maxHeight: '60vh', overflowY: 'auto' }}>
                    {historicoData.length > 0 ? (
                        <Timeline
                            mode="left"
                            items={historicoData.map(item => ({
                                color: item.color,
                                label: <Text type="secondary" style={{ fontSize: 12 }}>{item.date}</Text>,
                                children: (
                                    <div style={{ paddingBottom: 16 }}>
                                        <Text strong style={{ fontSize: 14 }}>{item.title}</Text>
                                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                                            {item.desc}
                                        </div>
                                    </div>
                                )
                            }))}
                        />
                    ) : (
                        <Empty description="Nenhum registro no histórico." />
                    )}
                </div>
            </Modal>

            <style>{`
                .accounting-table .ant-table-thead > tr > th {
                    background-color: #f8fafc;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    color: #64748b;
                    border-bottom: 2px solid #e2e8f0;
                }
                .accounting-table .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f1f5f9;
                    padding: 12px 16px;
                }
                .accounting-table .ant-table-tbody > tr:hover > td {
                    background-color: #f1f5f9 !important;
                }
                .premium-modal-v2 .ant-modal-content {
                    border-radius: 12px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                }
            `}</style>
        </Modal>
    );
}
